import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Typography, Space, Badge, Popover, List, Button } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  ShoppingOutlined,
  ApiOutlined,
  ProductOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  ProfileOutlined,
  WarningOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  CalendarOutlined,
  PictureOutlined,
  WalletOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { UsageService, UsageWarning } from '../services/usageService';
import { CreditService, CreditBalance } from '../services/creditService';
import { getActiveBrandId } from '../config/api';
import './Layout.css';

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [usageWarnings, setUsageWarnings] = useState<UsageWarning[]>([]);
  const [creditBalance, setCreditBalance] = useState<CreditBalance | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const effectiveCollapsed = isMobile ? !mobileMenuOpen : collapsed;
  
  const activeBrandId = getActiveBrandId(user);

  // Check usage warnings periodically
  useEffect(() => {
    if (user && activeBrandId) {
      const checkUsageWarnings = async () => {
        try {
          const warnings = await UsageService.getWarnings(activeBrandId);
          setUsageWarnings(warnings);
        } catch (error) {
          console.error('Error checking usage warnings:', error);
        }
      };

      // Check immediately
      checkUsageWarnings();

      // Check every minute
      const interval = setInterval(checkUsageWarnings, 60000);

      return () => clearInterval(interval);
    }
  }, [user, activeBrandId]);

  useEffect(() => {
    if (user && activeBrandId) {
      const loadCredits = async () => {
        try {
          const balance = await CreditService.getBalance(activeBrandId);
          setCreditBalance(balance);
        } catch (error) {
          console.error('Error loading credit balance:', error);
        }
      };

      loadCredits();
      const interval = setInterval(loadCredits, 60000);
      return () => clearInterval(interval);
    }
  }, [user, activeBrandId]);

  // Define all menu items with role-based access
  const allMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      roles: [], // Empty array means accessible to all roles
    },
    {
      key: '/brand-profile',
      icon: <ProfileOutlined />,
      label: 'Marka Profilim',
      roles: ['admin', 'owner', 'manager'], // Admin, Owner and Manager
    },
    {
      key: '/brands',
      icon: <ShoppingOutlined />,
      label: 'Marka Yönetimi',
      roles: ['admin'], // Admin only
    },
    {
      key: '/products',
      icon: <ProductOutlined />,
      label: 'Ürünlerim',
      roles: [], // All roles
    },
    {
      key: '/size-charts',
      icon: <TableOutlined />,
      label: 'Kalıp Tabloları',
      roles: ['admin', 'owner', 'manager'],
    },
    {
      key: '/product-to-image',
      icon: <PictureOutlined />,
      label: 'Product Studio',
      roles: [], // All roles
    },
    {
      key: '/feature-usage',
      icon: <ThunderboltOutlined />,
      label: 'Kullanım & Limitler',
      roles: [], // All roles
    },
    {
      key: '/credits',
      icon: <WalletOutlined />,
      label: 'Krediler',
      roles: [], // All roles
    },
    {
      key: '/analytics',
      icon: <BarChartOutlined />,
      label: 'Analitik Merkezi',
      roles: [], // All roles
    },
    {
      key: '/waiting-config',
      icon: <SettingOutlined />,
      label: 'Bekleme Ayarları',
      roles: [], // All roles
    },
    {
      key: '/feature-pricing',
      icon: <DollarOutlined />,
      label: 'Fiyatlandırma & Fatura',
      roles: ['admin'], // Admin only
    },
    {
      key: '/admin/credits',
      icon: <WalletOutlined />,
      label: 'Kredi Tanımlama',
      roles: ['admin'],
    },
    {
      key: '/brand-integration',
      icon: <ApiOutlined />,
      label: 'Brand Entegrasyonu',
      roles: [], // Admin only
    },
    {
      key: '/admin/appointments',
      icon: <CalendarOutlined />,
      label: 'Randevu Yönetimi',
      roles: ['admin'], // Admin only
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Ayarlar',
      roles: [], // All roles
    },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems
    .filter(item => {
      // If roles array is empty, item is accessible to all
      if (item.roles.length === 0) return true;
      // Otherwise, check if user's role is in the allowed roles
      return user?.role && item.roles.includes(user.role);
    })
    .map(({ roles, ...item }) => item); // Remove roles from final menu items

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profilim',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Çıkış Yap',
      onClick: logout,
    },
  ];

  return (
    <AntLayout className="dashboard-shell" style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        breakpoint="lg"
        collapsedWidth={isMobile ? 0 : 80}
        collapsed={effectiveCollapsed}
        onBreakpoint={broken => {
          setIsMobile(broken);
          if (!broken) setMobileMenuOpen(false);
        }}
        style={{
          overflow: isMobile && !mobileMenuOpen ? 'hidden' : 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
        }}
      >
        <div style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: effectiveCollapsed ? 16 : 18,
          fontWeight: 'bold',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          {effectiveCollapsed ? '🛍️' : '🛍️ Fashion Connect'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            if (isMobile) setMobileMenuOpen(false);
          }}
        />
      </Sider>
      <AntLayout
        className="dashboard-main-layout"
        style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 200), transition: 'all 0.2s' }}
      >
        <Header
          className="dashboard-header"
          style={{
            padding: '0 24px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          }}
        >
          <div className="dashboard-header-leading" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Button
              aria-label={effectiveCollapsed ? 'Navigasyon menüsünü aç' : 'Navigasyon menüsünü kapat'}
              type="text"
              icon={effectiveCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => {
                if (isMobile) setMobileMenuOpen(current => !current);
                else setCollapsed(current => !current);
              }}
            />
            <Text className="dashboard-header-title" strong style={{ fontSize: 16 }}>
              Fashion Connect Dashboard
            </Text>
          </div>
          <Space className="dashboard-header-actions" size={24}>
            <Button
              className="dashboard-credit-button"
              icon={<WalletOutlined />}
              onClick={() => navigate('/credits')}
              type={(creditBalance?.availableCredits ?? 0) <= 5 ? 'primary' : 'default'}
            >
              {creditBalance?.availableCredits ?? 0} Kredi
            </Button>
            <Popover
              content={
                usageWarnings.length > 0 ? (
                  <div style={{ width: 300 }}>
                    <List
                      size="small"
                      header={<Text strong>Kullanım Limit Uyarıları</Text>}
                      dataSource={usageWarnings}
                      renderItem={(warning) => (
                        <List.Item>
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text strong>{warning.featureName}</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Kullanım: {warning.usageCount} / {warning.baseQuota}
                            </Text>
                            <Text type="danger" style={{ fontSize: 12 }}>
                              Aşım: +{warning.overageCount}
                            </Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                    <div style={{ marginTop: 8, textAlign: 'center' }}>
                      <Button type="link" onClick={() => navigate('/feature-usage')} style={{ padding: 0 }}>
                        Detaylı Görüntüle
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Text type="secondary">Hiç uyarı yok</Text>
                )
              }
              title="Kullanım Uyarıları"
              trigger="click"
            >
              <Badge 
                count={usageWarnings.length} 
                showZero={false}
                style={{ backgroundColor: '#faad14' }}
              >
                <WarningOutlined 
                  style={{ 
                    fontSize: 18, 
                    cursor: 'pointer',
                    color: usageWarnings.length > 0 ? '#faad14' : undefined
                  }} 
                />
              </Badge>
            </Popover>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space className="dashboard-user-menu" style={{ cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <div className="dashboard-user-details" style={{lineHeight: '20px'}}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{user?.name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {user?.role === 'admin' && 'Admin'}
                    {user?.role === 'owner' && 'Owner'}
                    {user?.role === 'manager' && 'Manager'}
                    {user?.role === 'employee' && 'Employee'}
                  </div>
                </div>
              </Space>
            </Dropdown>
          </Space>
        </Header>
        <Content
          className="dashboard-content"
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: '#fff',
            borderRadius: 8,
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
